// src/components/AddressAutocompleteInput.jsx
//
// Drop-in replacement for a plain "Address" text input. Suggests real addresses
// via Places API (New) as the user types; selecting one auto-fills the address,
// postcode, and coordinates. Typing without selecting a suggestion still works
// as plain free text (e.g. a new-build site Google doesn't have on record yet) -
// coordinates are simply left for the server to try to resolve.

import React, { useState, useRef, useEffect } from 'react';
import useAddressAutocomplete from '../hooks/useAddressAutocomplete';

const inputStyle = {
  padding: '12px',
  border: '1px solid #cbd5e1',
  borderRadius: '8px',
  width: '100%',
  boxSizing: 'border-box',
};

export default function AddressAutocompleteInput({
  address,
  setAddress,
  setPostcode,
  setLatLng,
  placeholder = 'Address',
}) {
  const [inputValue, setInputValue] = useState(address || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const { suggestions, search, selectPlace, clearSuggestions } = useAddressAutocomplete();
  const containerRef = useRef(null);

  useEffect(() => {
    setInputValue(address || '');
  }, [address]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setAddress(value);
    setLatLng(null, null); // manual edit invalidates any previously-selected coordinates
    setShowDropdown(true);
    search(value);
  };

  const handleSelect = async (suggestion) => {
    const { placeId, text } = suggestion.placePrediction;
    setInputValue(text.text);
    setShowDropdown(false);
    clearSuggestions();

    const details = await selectPlace(placeId);
    if (details) {
      setInputValue(details.address);
      setAddress(details.address);
      if (details.postcode) setPostcode(details.postcode);
      setLatLng(details.latitude, details.longitude);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChange}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
        required
        autoComplete="off"
        style={inputStyle}
      />
      {showDropdown && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'var(--cc-card-bg)',
            border: '1px solid var(--branding-app-surface-panel-border)',
            borderRadius: '8px',
            marginTop: '4px',
            zIndex: 1100,
            maxHeight: '240px',
            overflowY: 'auto',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          }}
        >
          {suggestions.map((s) => (
            <div
              key={s.placePrediction.placeId}
              onClick={() => handleSelect(s)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                color: 'var(--cc-text-primary)',
                borderBottom: '1px solid var(--branding-app-surface)',
              }}
            >
              {s.placePrediction.text.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
